// Converts the hierarchy from Redux into React Flow nodes + edges
// Layout: top-down tree with Vision at top

export const NODE_W = { vision: 280, milestone: 220, action: 200, subnode: 180 };
// NODE_H values are initial estimates only — PlanningCanvas does a second pass
// after React Flow measures actual rendered heights to correct sub-node positions.
export const NODE_H = { vision: 100, milestone: 160, action: 220, subnode: 80 };
export const GAP_X = 28;
export const GAP_Y = { vm: 100, ma: 90, as: 24 };

function subTreeWidth(subNodes) {
  if (!subNodes?.length) return NODE_W.action;
  const total = subNodes.reduce((s, _) => s + NODE_W.subnode + GAP_X, -GAP_X);
  return Math.max(NODE_W.action, total);
}

function milestoneTreeWidth(m) {
  if (!m.actions?.length) return NODE_W.milestone;
  const aWidths = m.actions.map(a => subTreeWidth(a.subNodes));
  const total = aWidths.reduce((s, w) => s + w + GAP_X, -GAP_X);
  return Math.max(NODE_W.milestone, total);
}

export function buildGraphFromHierarchy(vision, milestones) {
  const nodes = [];
  const edges = [];

  if (!vision) return { nodes, edges };

  const visionId = `vision-${vision._id}`;

  const mWidths = milestones.map(milestoneTreeWidth);
  const totalWidth = mWidths.length
    ? mWidths.reduce((s, w) => s + w + GAP_X, -GAP_X)
    : NODE_W.milestone;

  // Vision node (centered)
  nodes.push({
    id: visionId,
    type: 'visionNode',
    position: { x: -NODE_W.vision / 2, y: 0 },
    data: { vision },
  });

  let curX = -totalWidth / 2;

  milestones.forEach((m, mi) => {
    const mWidth = mWidths[mi];
    const mCenterX = curX + mWidth / 2;
    const mId = `milestone-${m._id}`;
    const mY = NODE_H.vision + GAP_Y.vm;

    nodes.push({
      id: mId,
      type: 'milestoneNode',
      position: { x: mCenterX - NODE_W.milestone / 2, y: mY },
      data: { milestone: m },
    });

    edges.push({
      id: `e-vision-${mId}`,
      source: visionId,
      target: mId,
      type: 'glowEdge',
      data: { color: '#f17501' },
    });

    const actions = m.actions || [];
    const aWidths = actions.map(a => subTreeWidth(a.subNodes));
    const aTotalWidth = aWidths.length
      ? aWidths.reduce((s, w) => s + w + GAP_X, -GAP_X)
      : 0;
    let aCurX = mCenterX - aTotalWidth / 2;

    actions.forEach((a, ai) => {
      const aWidth = aWidths[ai];
      const aCenterX = aCurX + aWidth / 2;
      const aId = `action-${a._id}`;
      const aY = mY + NODE_H.milestone + GAP_Y.ma;

      nodes.push({
        id: aId,
        type: 'actionNode',
        position: { x: aCenterX - NODE_W.action / 2, y: aY },
        data: { action: a },
      });

      edges.push({
        id: `e-${mId}-${aId}`,
        source: mId,
        target: aId,
        type: 'glowEdge',
        data: { color: '#10b981' },
      });

      const subs = a.subNodes || [];
      const sTotalWidth = subs.length
        ? subs.reduce((s, _) => s + NODE_W.subnode + GAP_X, -GAP_X)
        : 0;
      let sCurX = aCenterX - sTotalWidth / 2;

      subs.forEach((s) => {
        const sId = `sub-${s._id}`;
        const sY = aY + NODE_H.action + GAP_Y.as;

        nodes.push({
          id: sId,
          type: 'subNode',
          position: { x: sCurX, y: sY },
          data: { node: s },
        });

        edges.push({
          id: `e-${aId}-${sId}`,
          source: aId,
          target: sId,
          type: 'glowEdge',
          data: { color: subNodeColor(s.type) },
        });

        sCurX += NODE_W.subnode + GAP_X;
      });

      aCurX += aWidth + GAP_X;
    });

    curX += mWidth + GAP_X;
  });

  return { nodes, edges };
}

export function subNodeColor(type) {
  const map = {
    node:       '#94a3b8',
    obstacle:   '#ef4444',
    doubt:      '#f59e0b',
    assumption: '#f17501',
    belief:     '#06b6d4',
    decision:   '#3b82f6',
    note:       '#64748b',
    custom:     '#94a3b8',
  };
  return map[type] || '#94a3b8';
}
